import {
  MutableRefObject,
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  IComposableAnalyticsCharts,
  IComposableDashboard,
  IComposableDashboardCollection,
  IComposableDashboardCollectionsContext,
  IComposableDashboardContext,
  fetchDashboardCollections,
  fetchDashboardsByIds,
} from 'wisestep-api';

import { useWorkspaceContext } from '../../../context';
import {
  defaultDashboardCollection,
  getDefaultDashboardValues,
} from '../helper/getDefaultDashboardValues';
import { useComposableDashboardsContext } from '../hooks/useComposableDashboardsContext';

export const ComposableDashboardCollectionsContext =
  createContext<IComposableDashboardCollectionsContext | null>(null);

export default function ComposableDashboardsCollectionsContextProvider(
  props: PropsWithChildren<IComposableDashboardCollectionsContext | null>
) {
  const [dashboardCollections, setDashboardCollections] = useState<
    IComposableDashboardCollection[]
  >([]);
  const [selectedDashboardCollection, setSelectedCollection] =
    useState<IComposableDashboardCollection>(defaultDashboardCollection);
  const { setSelectedDashboard, setLoader } =
    useComposableDashboardsContext() as Required<IComposableDashboardContext>;
  const workspace = useWorkspaceContext();

  const setSelectedCollectionByIndex = useCallback(
    async (index: number, fetchAndSetDashboards?: boolean, overridingFilters?: any) => {
      if (index == -1) {
        setSelectedCollection(defaultDashboardCollection);
        return;
      }
      if (fetchAndSetDashboards) {
        const currentDashboardCollection = dashboardCollections[index];
        if (overridingFilters)
          currentDashboardCollection.appliedDashboardFilters = overridingFilters;
        setSelectedCollection({ ...currentDashboardCollection });
        const dashboardIds = dashboardCollections[index].dashboardIds;
        try {
          setLoader(true);
          const fetchedDashboards = await fetchDashboardsAndSetInCollection(dashboardIds);
          if (overridingFilters) {
            const updatedDashboards = fetchedDashboards.map((db) => {
              const filters = { ...db.appliedDashboardFilters, ...overridingFilters };
              db.appliedDashboardFilters = filters;
              return db;
            });
            setSelectedCollection({
              ...dashboardCollections[index],
              dashboards: updatedDashboards,
            });
          } else {
            setSelectedCollection({
              ...dashboardCollections[index],
              dashboards: fetchedDashboards,
            });
          }
          setLoader(false);
        } catch (e) {
          console.log(e);
          setLoader(false);
        }
      } else setSelectedCollection(dashboardCollections[index]);
    },
    [dashboardCollections, setLoader]
  );

  const fetchDashboardsAndSetInCollection = async (ids: string[]) => {
    const response = await fetchDashboardsByIds(ids);
    (response.data as IComposableDashboard[])?.forEach((userDashboard) => {
      const charts = JSON.parse(userDashboard.charts as string) as IComposableAnalyticsCharts[];
      charts.forEach((chart) => !chart.id && (chart.id = uuidv4()));
      userDashboard.charts = JSON.stringify(charts);
    });
    return response.data as IComposableDashboard[];
  };

  const setSelectedCollectionDashboardByIndex = useCallback(
    (index: number, filtersRefreshBoolRef?: MutableRefObject<boolean>) => {
      const dashboards = selectedDashboardCollection.dashboards;
      if (index !== -1) {
        const charts: IComposableAnalyticsCharts[] =
          dashboards[index]?.charts.length > 0
            ? JSON.parse(dashboards[index]?.charts as string)
            : [];
        setSelectedDashboard({
          ...dashboards[index],
          charts,
        });
        if (charts.length > 0) {
          if (filtersRefreshBoolRef) {
            filtersRefreshBoolRef.current = true;
            setLoader(true);
          }
        }
      } else {
        const defaultValues = getDefaultDashboardValues();
        defaultValues.workspaceId = workspace?.id ?? '';
        defaultValues.createdBy = workspace.userId ?? '';
        setSelectedDashboard(defaultValues);
      }
    },
    [
      selectedDashboardCollection.dashboards,
      setLoader,
      setSelectedDashboard,
      workspace?.id,
      workspace.userId,
    ]
  );

  const refetchDashboardCollections = useCallback(async () => {
    try {
      const response = await fetchDashboardCollections();
      const dashboardCollections: IComposableDashboardCollection[] = response.data;
      setDashboardCollections(dashboardCollections);
    } catch (e) {
      console.error('Error fetching dashboard collections:', e);
    }
  }, []);

  useEffect(() => {
    refetchDashboardCollections();
  }, [refetchDashboardCollections]);

  return (
    <ComposableDashboardCollectionsContext.Provider
      value={{
        dashboardCollections,
        setDashboardCollections,
        selectedDashboardCollection,
        setSelectedCollection,
        setSelectedCollectionByIndex,
        setSelectedCollectionDashboardByIndex,
        refetchDashboardCollections,
      }}
    >
      {props.children}
    </ComposableDashboardCollectionsContext.Provider>
  );
}
